import React from 'react';
import { useSelector } from 'react-redux';
import { getSelectedProduct } from 'ducks/products';
import { useCore } from 'contexts/core';
import styled from 'styled-components';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { useIntl } from 'react-intl';
import { LightsArrangement } from './types/LightsArrangement';
import getLightsArrangement from './utils/getLightsArrangement';
import applyNumberOfLights from './utils/applyNumberOfLights';

const LightPicker = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${({ theme }) => theme.space.xl};
`;

const InputWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  border-radius: 50%;
  height: ${({ theme }) => theme.space.xl};
  width: ${({ theme }) => theme.space.xl};
  margin-right: ${({ theme }) => theme.space.xs};

  &:hover {
    background: ${({ theme }) => theme.colors.grey[100]};
  }
`;

const RadioButton = styled.input`
  opacity: 0;
  z-index: 1;
  position: absolute;
  cursor: pointer;
`;

// prettier-ignore
const RadioButtonDot = styled.div`
  height: ${({ theme }) => theme.space.l};
  width: ${({ theme }) => theme.space.l};
  border-radius: 50%;
  border: 3px solid white;
  background: white;
  box-shadow: 0 0 0 1px black;
  outline-offset: 3;

  &:hover {
    background: ${({ theme }) => theme.colors.grey[100]};
  }

  ${/* sc-selector */ RadioButton}:checked ~ & {
    background: black;
  }

  ${/* sc-selector */ RadioButton}[data-focus-visible-added]:focus ~ & {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.feedback.focus};
  }
`;

const OptionLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const LightsOptions = () => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const selectedProduct = useSelector(getSelectedProduct);
  const selectedLightArrangement = !selectedEntity
    ? LightsArrangement.None
    : getLightsArrangement(selectedEntity, coreApi);

  const onOptionChange = () => {
    if (selectedEntity && selectedProduct) {
      applyNumberOfLights(
        selectedEntity,
        selectedProduct.coreProductId,
        coreApi,
        selectedLightArrangement
      );
    }
  };

  if (selectedLightArrangement === LightsArrangement.None) {
    return null;
  }

  return (
    <LightPicker data-testid="light-picker">
      <OptionLabel>
        <InputWrapper>
          <RadioButton
            type="radio"
            value={LightsArrangement.Single}
            checked={selectedLightArrangement === LightsArrangement.Single}
            onChange={onOptionChange}
          />
          <RadioButtonDot />
        </InputWrapper>
        <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.single' })}
        </Body>
      </OptionLabel>
      <OptionLabel>
        <InputWrapper>
          <RadioButton
            type="radio"
            value={LightsArrangement.Double}
            checked={selectedLightArrangement === LightsArrangement.Double}
            onChange={onOptionChange}
          />
          <RadioButtonDot />
        </InputWrapper>
        <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.double' })}
        </Body>
      </OptionLabel>
    </LightPicker>
  );
};

export default LightsOptions;
